/**
 * Couverture MESURÉE du corpus de jurisprudence administrative d'Hector (`admin_decisions`).
 *
 * Source unique : la description de `search_jurisprudence`, la vérification JP du chat
 * (`search_jp_by_id`, règles de citation) et la recherche JP du pipeline d'acte lisent
 * toutes cette constante. Elle n'est PAS une propriété du droit ni des sources publiques :
 * c'est l'état de NOTRE chargement, appelé à changer dès la reprise de l'ingestion.
 *
 * Why (churn de Me Guerry, octobre 2026) : sans cette connaissance, l'agent concluait
 * « aucune jurisprudence pertinente » sur des contentieux balisés dont toute la
 * jurisprudence d'appel tombe entre 2005 et 2022, recopiait sans contrôle des arrêts de
 * CAA absents du corpus, et comblait le trou avec des arrêts de 1982-2004 rendus sous des
 * textes abrogés. Le trou CAA 2005-2022 est INTERNE : ces arrêts sont publics sur
 * Légifrance (fonds CETAT / JADE, ex. 10NT01980 = CETATEXT000025704417, 21MA04763 =
 * CETATEXT000045795712), seul notre chargement du 22/05/2026 s'arrête à août 2005.
 *
 * Mise à jour : rejouer la requête de lecture
 * `apps/backend/services/jp-corpus/corpus-coverage/measure-admin-corpus-coverage.sql`
 * sur la base OVH (lecture seule), recopier les bornes ci-dessous et la date de mesure.
 * Ne jamais écrire ces chiffres ailleurs : les prompts les reçoivent de
 * `describeAdminCorpusCoverage()`.
 */

/** Juridiction administrative telle que la recherche et la vérification la codent. */
export type AdminJurisdiction = 'CE' | 'CAA' | 'TA';

/** Une plage continue de décisions chargées (dates de décision, bornes incluses). */
export interface AdminCoverageRange {
  /** `YYYY-MM-DD`, première décision chargée. */
  from: string;
  /** `YYYY-MM-DD`, dernière décision chargée. */
  to: string;
  /** Source de chargement (`cetat` = Légifrance CETAT ; `opendata` = opendata.justice-administrative.fr). */
  source: 'cetat' | 'opendata';
  /** Nombre de décisions dans la plage, au jour de la mesure. */
  count: number;
  /** Plage trop clairsemée pour qu'une absence de résultat y signifie quoi que ce soit. */
  sparse?: true;
}

export interface AdminCorpusCoverage {
  /** Jour de la mesure (`YYYY-MM-DD`). */
  measuredAt: string;
  /** Requête de lecture qui a produit ces bornes, chemin relatif à la racine du repo. */
  measureQuery: string;
  ranges: Readonly<Record<AdminJurisdiction, readonly AdminCoverageRange[]>>;
}

/**
 * Mesure du 2026-10-27 (lecture prod, `measure-admin-corpus-coverage.sql`) :
 * min/max de `decision_date` et effectifs par (source, juridiction).
 */
export const ADMIN_CORPUS_COVERAGE: AdminCorpusCoverage = {
  measuredAt: '2026-10-27',
  measureQuery: 'apps/backend/services/jp-corpus/corpus-coverage/measure-admin-corpus-coverage.sql',
  ranges: {
    CE: [
      { from: '1875-02-19', to: '2026-05-20', source: 'cetat', count: 149_930 },
      { from: '2021-06-10', to: '2025-10-10', source: 'opendata', count: 32_031 },
    ],
    CAA: [
      { from: '1989-01-31', to: '2005-08-30', source: 'cetat', count: 74_364 },
      { from: '2022-03-22', to: '2025-10-09', source: 'opendata', count: 96_697 },
    ],
    TA: [
      { from: '1967-04-07', to: '2009-12-17', source: 'cetat', count: 204, sparse: true },
      { from: '2022-06-30', to: '2025-10-09', source: 'opendata', count: 670_290 },
    ],
  },
};

/** Verdict de couverture pour une décision ou une période donnée. */
export type AdminCoverageVerdict =
  /** La date tombe dans une plage chargée (dense). */
  | 'covered'
  /** La date tombe hors de toute plage dense : une absence au corpus ne prouve rien. */
  | 'not_covered'
  /** Date inconnue : on ne peut pas trancher. */
  | 'unknown';

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function denseRanges(jurisdiction: AdminJurisdiction, coverage: AdminCorpusCoverage): AdminCoverageRange[] {
  return coverage.ranges[jurisdiction].filter((r) => !r.sparse);
}

/**
 * La décision de `jurisdiction` rendue le `dateIso` (ou, à défaut, l'année `year`)
 * tombe-t-elle dans une plage chargée ? Une année est couverte si elle recoupe une plage.
 */
export function adminCoverageVerdict(
  jurisdiction: AdminJurisdiction,
  when: { dateIso?: string | null; year?: number | null },
  coverage: AdminCorpusCoverage = ADMIN_CORPUS_COVERAGE,
): AdminCoverageVerdict {
  const ranges = denseRanges(jurisdiction, coverage);
  if (when.dateIso && ISO_DAY.test(when.dateIso)) {
    return ranges.some((r) => when.dateIso! >= r.from && when.dateIso! <= r.to) ? 'covered' : 'not_covered';
  }
  if (typeof when.year === 'number' && Number.isInteger(when.year)) {
    const y = when.year;
    return ranges.some((r) => y >= Number(r.from.slice(0, 4)) && y <= Number(r.to.slice(0, 4)))
      ? 'covered'
      : 'not_covered';
  }
  return 'unknown';
}

/**
 * Année d'enregistrement portée par un numéro de requête administratif, quand il en porte une.
 *
 * - CAA : « 16MA00483 », « 23NT00970 » → 2016, 2023 (deux chiffres d'année + code de cour).
 * - TA : « 2404071 » (7 chiffres) → 2024.
 * - CE : numéro séquentiel (« 386624 ») → aucune année lisible.
 *
 * L'année d'enregistrement précède la décision d'un à trois ans : c'est un indice pour
 * signaler un trou de couverture, jamais une date de décision.
 */
export function registrationYearFromAdminNumber(number: string): number | null {
  const compact = number.replace(/\s/g, '').toUpperCase();
  const caa = /^(\d{2})[A-Z]{2}\d{4,6}$/.exec(compact);
  if (caa) return 2000 + Number(caa[1]);
  const ta = /^(\d{2})\d{5}$/.exec(compact);
  if (ta) return 2000 + Number(ta[1]);
  return null;
}

function frenchDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/** Plages denses d'une juridiction en clair, ex. « 31/01/1989 → 30/08/2005 et 22/03/2022 → 09/10/2025 ». */
export function describeAdminRanges(
  jurisdiction: AdminJurisdiction,
  coverage: AdminCorpusCoverage = ADMIN_CORPUS_COVERAGE,
): string {
  return denseRanges(jurisdiction, coverage)
    .map((r) => `${frenchDate(r.from)} → ${frenchDate(r.to)}`)
    .join(' et ');
}

/**
 * Bloc de prompt qui décrit la couverture et le comportement qui en découle. Une seule
 * formulation, partagée par le chat et le pipeline d'acte, pour que l'agent ne
 * réinvente pas une couverture fausse (« rien avant 2022 » est faux pour le Conseil d'État).
 */
export function describeAdminCorpusCoverage(coverage: AdminCorpusCoverage = ADMIN_CORPUS_COVERAGE): string {
  return `Couverture du corpus de jurisprudence ADMINISTRATIVE d'Hector (mesurée le ${frenchDate(coverage.measuredAt)}, appelée à s'étendre) :
- Conseil d'État : ${describeAdminRanges('CE', coverage)}.
- Cours administratives d'appel : ${describeAdminRanges('CAA', coverage)} — AUCUN arrêt de CAA entre septembre 2005 et mars 2022.
- Tribunaux administratifs : ${describeAdminRanges('TA', coverage)} (avant, quelques décisions isolées seulement).
Conséquences :
- Une recherche CAA ou TA sans résultat sur une période non couverte ne prouve PAS l'absence de jurisprudence : dis « hors couverture du corpus d'Hector pour cette période », jamais « aucune jurisprudence pertinente ».
- Une décision administrative absente du corpus est « non vérifiée (hors corpus) », jamais « vérifiée », et ne se présente pas comme sûre (ni en annexe, ni dans un tableau de références).
- Ne comble jamais ce trou par de la jurisprudence ancienne rendue sous des textes depuis modifiés, ni par des décisions citées de mémoire.`;
}

/**
 * Phrase factuelle à joindre à une recherche administrative SANS résultat, pour que le lecteur
 * (agent du chat, rédacteur de plan, avocat) sache qu'une partie de la jurisprudence n'a pas
 * pu être trouvée. `null` si aucune juridiction à couverture trouée (CAA, TA) n'est visée.
 * Accepte les codes en majuscules ou minuscules.
 */
export function adminCoverageGapSentence(
  jurisdictions: readonly string[],
  coverage: AdminCorpusCoverage = ADMIN_CORPUS_COVERAGE,
): string | null {
  const codes = new Set(jurisdictions.map((j) => j.toUpperCase()));
  const parts: string[] = [];
  if (codes.has('CAA')) parts.push(`les cours administratives d'appel que du ${describeAdminRanges('CAA', coverage)}`);
  if (codes.has('TA')) parts.push(`les tribunaux administratifs que du ${describeAdminRanges('TA', coverage)}`);
  if (parts.length === 0) return null;
  return `Le corpus d'Hector ne couvre ${parts.join(', et ')} : une jurisprudence rendue hors de ces périodes n'a pas pu être trouvée par cette recherche.`;
}
